import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { BookOpen, Dumbbell, Sparkles, Star } from 'lucide-react';

export default function CurriculumSection({ curriculum }) {
  const [activeWeek, setActiveWeek] = useState('week1');

  // Group curriculum by week
  const weeks = {
    week1: curriculum.filter(d => d.week === 1),
    week2: curriculum.filter(d => d.week === 2),
    week3: curriculum.filter(d => d.week === 3)
  };

  const weekTitles = {
    week1: 'Week 1: Foundation',
    week2: 'Week 2: Animal Forms',
    week3: 'Week 3: Integration'
  };


  const getIcon = (type) => {
    switch (type) {
      case 'knowledge': return <BookOpen className="h-4 w-4 text-[#2C5E4B]" />;
      case 'practice': return <Dumbbell className="h-4 w-4 text-[#9A3B26]" />;
      case 'relaxation': return <Sparkles className="h-4 w-4 text-[#DDA15E]" />;
      case 'special': return <Star className="h-4 w-4 text-[#9A3B26]" />;
      default: return null;
    }
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-center text-[#292524] mb-4">
          21-Day Curriculum
        </h2>
        <p className="text-center text-[#78716C] mb-12 max-w-2xl mx-auto">
          A carefully crafted journey from foundation to mastery
        </p>

        <Tabs value={activeWeek} onValueChange={setActiveWeek} className="w-full">
          <TabsList className="grid grid-cols-3 mb-8 bg-[#F4EFE6] p-1 rounded-full">
            {Object.keys(weeks).map((week, idx) => (
              <TabsTrigger
                key={week}
                value={week}
                data-testid={`curriculum-tab-${week}`}
                className="rounded-full data-[state=active]:bg-[#9A3B26] data-[state=active]:text-white transition-all duration-300"
              >
                Week {idx + 1}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(weeks).map(([weekKey, days]) => (
            <TabsContent key={weekKey} value={weekKey} className="mt-0">
              <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden">
                <div className="bg-[#9A3B26] text-white p-4">
                  <h3 className="font-heading text-xl font-bold">{weekTitles[weekKey]}</h3>
                </div>
                
                <Accordion type="single" collapsible className="w-full">
                  {days.map((day) => (
                    <AccordionItem key={day.day} value={`day-${day.day}`} className="border-b border-[#E7E5E4] last:border-0">
                      <AccordionTrigger 
                        data-testid={`curriculum-day-${day.day}`}
                        className="px-6 py-4 hover:no-underline hover:bg-[#F4EFE6]/50 transition-colors"
                      >
                        <div className="flex items-center gap-4 text-left">
                          <div className="w-12 h-12 bg-[#F4EFE6] rounded-xl flex items-center justify-center">
                            <span className="font-heading text-lg font-bold text-[#9A3B26]">{day.day}</span>
                          </div>
                          <div>
                            <h4 className="font-heading text-lg font-bold text-[#292524]">{day.title}</h4>
                            <p className="text-sm text-[#78716C]">
                              {[day.knowledge, day.practice].filter(Boolean).slice(0, 2).join(' • ')}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="grid sm:grid-cols-2 gap-4 ml-16">
                          {day.knowledge && (
                            <div className="flex items-start gap-3 p-3 bg-[#F4EFE6] rounded-lg">
                              {getIcon('knowledge')}
                              <div>
                                <p className="text-xs uppercase tracking-wider text-[#78716C] mb-1">Knowledge</p>
                                <p className="text-[#292524] font-medium">{day.knowledge}</p>
                              </div>
                            </div>
                          )}
                          {day.practice && (
                            <div className="flex items-start gap-3 p-3 bg-[#F4EFE6] rounded-lg">
                              {getIcon('practice')}
                              <div>
                                <p className="text-xs uppercase tracking-wider text-[#78716C] mb-1">Practice</p>
                                <p className="text-[#292524] font-medium">{day.practice}</p>
                              </div>
                            </div>
                          )}
                          {day.relaxation && (
                            <div className="flex items-start gap-3 p-3 bg-[#F4EFE6] rounded-lg">
                              {getIcon('relaxation')}
                              <div>
                                <p className="text-xs uppercase tracking-wider text-[#78716C] mb-1">Relaxation</p>
                                <p className="text-[#292524] font-medium">{day.relaxation}</p>
                              </div>
                            </div>
                          )}
                          {day.special && (
                            <div className="flex items-start gap-3 p-3 bg-[#DDA15E]/20 rounded-lg">
                              {getIcon('special')}
                              <div>
                                <p className="text-xs uppercase tracking-wider text-[#9A3B26] mb-1">Special</p>
                                <p className="text-[#292524] font-medium">{day.special}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
